import Button from '@ui/buttons/button/button';
import SlideDownCard from '@ui/cards/slide-down-card/slide-down-card';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { ICONS } from '@ui/icons/types';
import SelectSingle from '@ui/select/react-select/select-single';
import { useRef, useState, memo } from 'react';
import { timezoneOptions } from '../constants';
import Icon from '@ui/icons/icon';
import classes from '../campaign-editor.module.css';
import Popover from '@ui/popover/popover';
import DateRangePicker from '@ui/calendar/date-range-picker';
import type { DateRange } from '@ui/calendar/types';

interface Props {
  deliveryDate: number | null;
  timezone: string;
  handleTimezoneChange: (str: string) => void;
  handleDeliveryDateChange: (range: DateRange) => void;
}
function DateInputs({ deliveryDate, timezone, handleTimezoneChange, handleDeliveryDateChange }: Props) {
  const [schedulingOpen, setSchedulingOpen] = useState(true);
  const popoverPortal = useRef<HTMLDivElement | null>(null);
  return (
    <Container padding="0 12px">
      <Container
        style={{ transition: 'border-radius 350ms' }}
        borderRadius={schedulingOpen ? '16px' : '12px'}
        design="GlobalCont2"
      >
        <Container>
          <Button
            className="flex jc-space-between ai-center width-100-percent"
            onClick={() => setSchedulingOpen((prev) => !prev)}
          >
            <Text className="H6" design="GlobalIcoText1">
              Scheduling
            </Text>
            <div style={{ transform: schedulingOpen ? `rotate(90deg)` : undefined }}>
              <Icon name={ICONS.ANGLE_RIGHT} size={12} />
            </div>
          </Button>
        </Container>
        <SlideDownCard autoGrow open={schedulingOpen} animateOnInitialLoad={false}>
          <Container padding="0 0 8px 0">
            <div className={classes.fieldRow}>
              <Text design="GlobalIcoText3" tag="label" className="L2">
                Send date &amp; time
              </Text>
              <Popover
                preferredPosition="bottom-left"
                width="auto"
                portalEl={popoverPortal}
                overflow="visible"
                renderTriggerButton={() => (
                  <Button className="gap-8 flex ai-center" design="LocalBttnSec" width="100%">
                    <Icon name={ICONS.CALENDAR} size={14} color="var(--global-ico-text-2)" />
                    <span className={classes.dateCardText}>
                      {deliveryDate
                        ? new Date(deliveryDate).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })
                        : 'Select send date'}
                    </span>
                  </Button>
                )}
              >
                {({ triggerBounds }) => {
                  return (
                    <Container
                      style={{
                        position: 'fixed',
                        top: (triggerBounds?.top || 2) / 2 || 0,
                        left: triggerBounds?.width || 0,
                        zIndex: 1100,
                      }}
                      borderRadius="16px"
                    >
                      <DateRangePicker
                        selectSingleDate={true}
                        hideTimezone
                        borderRadius="16px"
                        value={{ startDate: deliveryDate, endDate: null, selectedTimezone: timezone }}
                        onChange={handleDeliveryDateChange}
                      />
                    </Container>
                  );
                }}
              </Popover>
            </div>
            <div className={classes.fieldRow}>
              <Text design="GlobalIcoText3" tag="label" className="L2">
                Timezone
              </Text>
              <SelectSingle
                value={timezone}
                options={timezoneOptions}
                onChange={handleTimezoneChange}
                placeholder="Select timezone"
                width="100%"
              />
            </div>
          </Container>
        </SlideDownCard>
      </Container>
    </Container>
  );
}

export default memo(DateInputs);
